/**
 * Series construction for the expanded PnL chart.
 *
 * Lives outside the card component so the contract it encodes is unit-testable:
 * the chart must LEAD with the same `portfolioPerformance` line the card's
 * sparkline draws, keeping realised and the open-leg estimate beneath it as
 * that number's breakdown. Expanding the card is then a zoom, not a second,
 * differently-shaped reading of the same day.
 */

import type { GetUserPnlChartRow } from '@morfi/backend-sdk-v2/client';

import type { PnlChartSeries } from '@/components/portfolio/cards/pnl-chart-card-liveline';
import type { PnlRange } from '@/components/portfolio/cards/pnl-chart-window';
import { D6_SCALE } from '@/config/solana';

type PnlLinePoint = GetUserPnlChartRow['realizedPnlLine'][number];

const EMPTY_SERIES: ReadonlyArray<PnlLinePoint> = [];

const COLOR_REALIZED = 'var(--outcome-blue-400)';
const COLOR_UNREALIZED_ESTIMATE = 'var(--outcome-purple-400)';
/** Signed like the card's sparkline, so expanding reads as the same line. */
const COLOR_PERFORMANCE_PROFIT = 'var(--data-profit)';
const COLOR_PERFORMANCE_LOSS = 'var(--data-loss)';

export interface CachedSeries {
  range: PnlRange;
  performance: ReadonlyArray<PnlLinePoint> | null;
  realized: ReadonlyArray<PnlLinePoint>;
  unrealizedEstimate: ReadonlyArray<PnlLinePoint>;
}

export interface PnlChartPresentation {
  readonly renderRange: PnlRange;
  readonly series: PnlChartSeries[];
  readonly formatValue: (value: number) => string;
}

function d6ToNumber(value: string): number {
  const parsed: number = Number(value);
  if (Number.isNaN(parsed)) return 0;
  return parsed / D6_SCALE;
}

export function buildPnlChartPresentation(args: {
  readonly range: PnlRange;
  readonly data: GetUserPnlChartRow | undefined;
  readonly isSuccess: boolean;
  readonly previous: CachedSeries | null;
  readonly performanceLabel: string;
  readonly realizedLabel: string;
  readonly unrealizedEstimateLabel: string;
  readonly formatBalance: (value: number, display?: 'full' | 'compact') => string;
  readonly formatD6: (value: string, display?: 'full') => string;
}): PnlChartPresentation {
  const { range, data, isSuccess, previous, formatBalance, formatD6 } = args;
  const hasCurrentData = isSuccess && data !== undefined;
  const renderRange = hasCurrentData ? range : (previous?.range ?? range);
  // The card's sparkline plots portfolio performance; the expanded view leads
  // with the same series so opening it is a zoom, not a different reading, and
  // keeps realised / open-leg beneath it as that number's breakdown. `null`
  // means the server could not measure the window — the card hides its delta
  // then, so the chart drops the line rather than substituting another.
  const performanceLine = hasCurrentData
    ? (data.portfolioPerformance?.line ?? null)
    : (previous?.performance ?? null);
  const realizedLine = hasCurrentData ? data.realizedPnlLine : (previous?.realized ?? EMPTY_SERIES);
  const unrealizedEstimateLine = hasCurrentData
    ? data.unrealizedPnlEstimateLine
    : (previous?.unrealizedEstimate ?? EMPTY_SERIES);
  const performancePoints = (performanceLine ?? EMPTY_SERIES).map((point) => ({
    time: Math.floor(new Date(point.timestamp).getTime() / 1000),
    value: d6ToNumber(point.value),
    valueD6: point.value,
  }));
  const realizedPoints = realizedLine.map((point) => ({
    time: Math.floor(new Date(point.timestamp).getTime() / 1000),
    value: d6ToNumber(point.value),
    valueD6: point.value,
  }));
  const unrealizedEstimatePoints = unrealizedEstimateLine.map((point) => ({
    time: Math.floor(new Date(point.timestamp).getTime() / 1000),
    value: d6ToNumber(point.value),
    valueD6: point.value,
  }));
  const exactD6ByPlotValue = new Map<number, string | null>();
  for (const point of [...performancePoints, ...realizedPoints, ...unrealizedEstimatePoints]) {
    const existing = exactD6ByPlotValue.get(point.value);
    if (existing === undefined) {
      exactD6ByPlotValue.set(point.value, point.valueD6);
    } else if (
      existing !== null &&
      formatD6(existing, 'full') !== formatD6(point.valueD6, 'full')
    ) {
      exactD6ByPlotValue.set(point.value, null);
    }
  }
  const formatValue = (value: number): string => {
    const exactD6 = exactD6ByPlotValue.get(value);
    return exactD6 === null || exactD6 === undefined
      ? formatBalance(value, 'full')
      : formatD6(exactD6, 'full');
  };
  const firstPerformance = performancePoints.at(0);
  const lastPerformance = performancePoints.at(-1);
  // Tone follows the WINDOW DELTA, not the level: the series is cumulative, so
  // a book that fell 500 → 200 is still positive at the last point while the
  // day was a loss. The card's sparkline tones off `portfolioPerformance.deltaD6`,
  // which the server computes as exactly this last − first.
  const performanceDelta = (lastPerformance?.value ?? 0) - (firstPerformance?.value ?? 0);
  const lastRealized = realizedPoints.at(-1);
  const lastUnrealizedEstimate = unrealizedEstimatePoints.at(-1);
  return {
    renderRange,
    formatValue,
    series: [
      ...(performanceLine === null
        ? []
        : [
            {
              id: 'performance',
              label: args.performanceLabel,
              color: performanceDelta < 0 ? COLOR_PERFORMANCE_LOSS : COLOR_PERFORMANCE_PROFIT,
              points: performancePoints,
              value: lastPerformance?.value ?? 0,
              valueLabel:
                lastPerformance === undefined ? formatD6('0') : formatD6(lastPerformance.valueD6),
            },
          ]),
      {
        id: 'realized',
        label: args.realizedLabel,
        color: COLOR_REALIZED,
        points: realizedPoints,
        value: lastRealized?.value ?? 0,
        valueLabel: lastRealized === undefined ? formatD6('0') : formatD6(lastRealized.valueD6),
      },
      {
        id: 'unrealized-estimate',
        label: args.unrealizedEstimateLabel,
        color: COLOR_UNREALIZED_ESTIMATE,
        dashed: true,
        points: unrealizedEstimatePoints,
        value: lastUnrealizedEstimate?.value ?? 0,
        valueLabel:
          lastUnrealizedEstimate === undefined
            ? formatD6('0')
            : formatD6(lastUnrealizedEstimate.valueD6),
      },
    ],
  };
}
